import * as React from 'react';
import {
  HealthProfilePageWrapper,
  MoreOptionsCard,
  MoreOptionsLink,
} from '@leagueplatform/health-profile-common';
import {
  StackLayout,
  Button,
  HeadingText,
  UtilityText,
  Box,
  StatusBadge,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  LoadingIndicator,
  Sidebar,
} from '@leagueplatform/web-common-components';
import {
  DATE_FORMAT,
  useFormatDateByUserProfileLocation,
} from '@leagueplatform/web-common';
import { track } from '@leagueplatform/analytics';
import { useInfiniteHealthMetricsV3 } from '#hooks/use-get-health-metrics-v3.hook.ts';
import type { InputConfigValue } from '@leagueplatform/dashboard-api';
import { ListItem } from '#components/list-item/index.ts';
import { ErrorPanel } from '#components/error-panel/index.ts';

import { HealthPrograms } from '#pages/details2/components/health-programs.tsx';
import { HealthLiteracyContent } from '#components/health-literacy/health-literacy-content.tsx';
import { useIsSelfReportedDataEnabled } from '#hooks/use-is-self-reported-data-enabled.hook.ts';
import { AddMetricDataButton } from '#pages/details2/components/add-metric-data-button.tsx';
import { AddEditDataModal } from '#components/add-edit-data/add-edit-data-modal.tsx';
import { NoDataAvailable } from '#pages/details3/components/no-data-available.tsx';
import { useHealthMetricDocumentTitle } from '#hooks/use-health-metric-document-title.ts';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { useDashboardConfig } from '../../dashboard.config';
import {
  useGroupAndFormatMetrics,
  type MetricWithConfigId,
} from './hooks/use-group-and-format-metrics.hook';
import { constructMetricLabel } from './utils/construct-metric-label.util';

type Params = {
  dataType: string;
};
const handleAnalytics = (metricType?: string) =>
  track('dashboard.screen_loaded.all_recorded_data_metric_type', {
    metric_type: metricType || '',
    metric_id: '',
    metric_status: '',
  });

export const AllRecordedData = () => {
  const { dataType } = useParams<Params>();
  const { formatMessage } = useIntl();
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();
  const { isSelfReportedDataEnabled } = useIsSelfReportedDataEnabled();
  const {
    isLoading: isLoadingHealthMetrics,
    isError: isErrorHealthMetrics,
    isRefetching: isRefetchingHealthMetrics,
    data: healthMetrics,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    hasNextPage,
    error,
  } = useInfiniteHealthMetricsV3({
    filter: {
      type: dataType,
    },
    fields: { type: ['type', 'value', 'source', 'unit', 'timestamp'] },
  });
  const { showFullTimeStamp } = useDashboardConfig();
  const metricDisplayName = healthMetrics?.pages[0].data[0].metricDisplayName;

  useHealthMetricDocumentTitle(
    formatMessage({
      id: 'ALL_RECORDED_DATA',
    }),
    metricDisplayName,
  );

  React.useEffect(() => {
    handleAnalytics(dataType);
  }, [dataType]);

  // Calculate displayConfigName
  const displayConfigName = React.useMemo(() => {
    if (!healthMetrics) {
      return false;
    }
    const allMetricConfigs =
      healthMetrics.pages[0].data[0].metricComponents.data;
    const firstConfigName = allMetricConfigs?.[0].name;
    // display the config name with the entry if there are multiple metric configs
    // and if the `name` field of the metric configs are different
    return (
      Boolean(allMetricConfigs?.length) &&
      !allMetricConfigs?.every(
        (metricConfig) => metricConfig.name === firstConfigName,
      )
    );
  }, [healthMetrics]);

  // Collect all metrics from all pages
  const allMetrics = React.useMemo<MetricWithConfigId[]>(
    () =>
      healthMetrics?.pages.flatMap((page) =>
        page.data.flatMap((metricConfig) =>
          metricConfig.values.data.map((dataMetric) => {
            const metricWithConfigId: MetricWithConfigId = {
              ...dataMetric,
              metricConfigId: metricConfig.id,
            };
            return metricWithConfigId;
          }),
        ),
      ) || [],
    [healthMetrics?.pages],
  );

  // Group metrics in a single pass
  const { groupedMetrics } = useGroupAndFormatMetrics({
    metrics: allMetrics,
  });

  if (isLoadingHealthMetrics && !healthMetrics) {
    return <LoadingIndicator />;
  }

  // TODO: implement handleEditClick functionality after completion of
  // edit data screens in https://everlong.atlassian.net/browse/ABTM-4654
  // const handleEditClick = () => {
  //   handleAnalytics( dataType, 'edit reading');
  // };

  const showHealthLiteracyOnAllRecordedDataScreen =
    healthMetrics?.pages[0].data[0].showHealthLiteracyOnAllRecordedDataScreen;
  const showAddDataButton =
    healthMetrics?.pages[0].data[0].metricComponents.data.some(
      (metricConfig) =>
        metricConfig.sourceType === 'self_reported' ||
        metricConfig.sourceType === 'self_reported_no_input',
    ) && isSelfReportedDataEnabled;

  const canConnectDevice =
    healthMetrics?.pages[0].data[0].metricComponents.data.some(
      (config) => config.sourceType === 'device',
    );
  const canSelfReport =
    healthMetrics?.pages[0].data[0].metricComponents.data.some(
      (config) => config.sourceType === 'self_reported',
    );

  // the user has no data for this metric
  const showEmptyState =
    healthMetrics?.pages[0].data[0].values.data.length === 0;

  return (
    <HealthProfilePageWrapper
      title={formatMessage({
        id: 'ALL_RECORDED_DATA',
      })}
      sidebar={
        <Sidebar>
          <MoreOptionsCard>
            <MoreOptionsLink
              to="data-sources"
              onClick={() =>
                track(
                  'dashboard.button_clicked.metric_item_metric_type.navigate_to_metric_tab',
                  {
                    metric_type: dataType,
                    metric_id: '',
                    metric_status: '',
                    content_id: '',
                    contentful_system: '',
                    detail: 'data sources',
                  },
                )
              }
            >
              {formatMessage({ id: 'VIEW_DATA_SOURCES' })}
            </MoreOptionsLink>
          </MoreOptionsCard>
          {showAddDataButton && healthMetrics ? (
            <AddEditDataModal
              metricConfigId={healthMetrics?.pages[0].data[0].id}
              inputConfig={healthMetrics?.pages[0].data[0].addInputConfig.data}
              modalTrigger={
                <AddMetricDataButton
                  onClick={() =>
                    track(
                      'dashboard.button_clicked.metric_item_metric_type.add_data',
                      {
                        metric_type: dataType,
                        metric_id: '',
                        is_empty_state: '',
                        content_id: '',
                        contentful_system: '',
                      },
                    )
                  }
                />
              }
            />
          ) : null}
        </Sidebar>
      }
    >
      <HeadingText
        size="xxl"
        level="2"
        css={{ paddingTop: '$oneAndHalf', paddingBottom: '$twoAndHalf' }}
      >
        {metricDisplayName}
      </HeadingText>
      <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
        {isErrorHealthMetrics ? (
          <ErrorPanel
            error={error}
            isRefetching={isRefetchingHealthMetrics}
            onRetry={refetch}
          />
        ) : null}

        {/* empty state */}
        {showEmptyState ? (
          <NoDataAvailable
            metric={healthMetrics.pages[0].data[0].metricDisplayName}
            canSelfRecordData={canSelfReport!}
            canConnectDevice={canConnectDevice!}
            metricConfigId={healthMetrics?.pages[0].data[0].id}
            inputConfig={healthMetrics?.pages[0].data[0].addInputConfig.data}
          />
        ) : null}
        {!isErrorHealthMetrics && !showEmptyState && healthMetrics && (
          <Box as="ul" css={{ width: '100%' }}>
            {Object.entries(groupedMetrics).map(
              ([groupingKey, formattedGroup]) => {
                const firstMetric: MetricWithConfigId =
                  formattedGroup.metrics[0];
                const {
                  sourceDisplayName,
                  timestamp,
                  metricComponent,
                  riskIndicator,
                  metricConfigId,
                } = firstMetric;
                const isSelfReported =
                  metricComponent.data.sourceType === 'self_reported';
                const date = formatDateWithUserProfileLocation(
                  timestamp,
                  DATE_FORMAT,
                );
                const displayTimestamp = formatDateWithUserProfileLocation(
                  timestamp,
                  {
                    hour: 'numeric',
                    minute: 'numeric',
                  },
                );
                const { referenceRange } = firstMetric.metricComponent.data;

                const { healthMetricInputs, defaultValuesForModal } =
                  formattedGroup;

                // Construct metric label for this group (comma-separated string)
                const metricValues = constructMetricLabel(
                  formattedGroup.metrics,
                  displayConfigName,
                );

                // Combine editInputConfigs from all metrics in the group
                const inputConfigForModal: InputConfigValue = {
                  ...firstMetric.editInputConfig.data,
                  healthMetricInputs,
                };

                return (
                  <ListItem as="li" key={groupingKey}>
                    <StackLayout>
                      {riskIndicator && (
                        <StatusBadge
                          status={riskIndicator?.style}
                          label={riskIndicator?.label}
                          showLight={false}
                        />
                      )}
                      {metricValues && (
                        <UtilityText as="p" emphasis="emphasized">
                          {metricValues}
                        </UtilityText>
                      )}
                      {referenceRange && (
                        <UtilityText
                          emphasis="subtle"
                          css={{ typography: '$caption' }}
                        >
                          {referenceRange}
                        </UtilityText>
                      )}
                      <UtilityText
                        emphasis="subtle"
                        css={{ typography: '$caption' }}
                      >
                        {sourceDisplayName}
                      </UtilityText>
                    </StackLayout>
                    <StackLayout
                      orientation="horizontal"
                      horizontalAlignment="end"
                      verticalAlignment="center"
                      spacing="$half"
                    >
                      <StackLayout
                        horizontalAlignment="end"
                        verticalAlignment="center"
                      >
                        <UtilityText
                          css={{
                            paddingTop: '$half',
                          }}
                        >
                          {date}
                        </UtilityText>
                        {showFullTimeStamp && (
                          <UtilityText
                            as="p"
                            emphasis="subtle"
                            css={{ typography: '$caption' }}
                          >
                            {displayTimestamp}
                          </UtilityText>
                        )}
                      </StackLayout>
                      {isSelfReported && isSelfReportedDataEnabled && (
                        <AddEditDataModal
                          metricConfigId={metricConfigId}
                          inputConfig={inputConfigForModal}
                          defaultValues={defaultValuesForModal}
                          timestamp={firstMetric.timestamp}
                          effectiveDateTime={firstMetric.effectiveTime}
                          modalTrigger={
                            <Button
                              priority="secondary"
                              quiet
                              icon="descriptiveCompose"
                              hideLabel
                            >
                              {formatMessage(
                                { id: 'EDIT_METRIC_DATA' },
                                {
                                  metric: metricDisplayName,
                                },
                              )}
                            </Button>
                          }
                        />
                      )}
                    </StackLayout>
                  </ListItem>
                );
              },
            )}

            {hasNextPage && (
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '$twoAndHalf',
                }}
              >
                <Button
                  priority="secondary"
                  disabled={isFetchingNextPage}
                  onClick={() => {
                    fetchNextPage();
                  }}
                >
                  {formatMessage({ id: 'LOAD_MORE' }).toLocaleUpperCase()}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {showHealthLiteracyOnAllRecordedDataScreen ? (
          <>
            <HealthPrograms metric={dataType} />
            <HealthLiteracyContent metric={dataType} />
          </>
        ) : null}
      </StackLayout>
    </HealthProfilePageWrapper>
  );
};
